/**
 * Scan d'un document par l'appareil photo (docs/UI.md § Scan de documents) :
 * contrôle de netteté et lecture du lien porté par le QR code des chartes.
 * Fonctions pures, pour être testées hors du navigateur.
 */

/** Niveaux de gris (luminance) à partir de pixels RGBA. */
export function niveauxDeGris(rgba: ArrayLike<number>): Uint8ClampedArray {
  const gris = new Uint8ClampedArray(Math.floor(rgba.length / 4));
  for (let i = 0; i < gris.length; i += 1) {
    const j = i * 4;
    gris[i] = 0.299 * rgba[j]! + 0.587 * rgba[j + 1]! + 0.114 * rgba[j + 2]!;
  }
  return gris;
}

/**
 * Variance du laplacien : mesure classique de netteté. Une photo floue a peu
 * de transitions franches, donc un laplacien presque constant et une variance
 * faible. À calculer sur une copie d'environ 1000 px de large, pour que le
 * seuil ne dépende pas de la résolution de l'appareil.
 */
export function varianceLaplacien(gris: ArrayLike<number>, largeur: number, hauteur: number): number {
  let somme = 0;
  let carres = 0;
  let nombre = 0;
  for (let y = 1; y < hauteur - 1; y += 1) {
    for (let x = 1; x < largeur - 1; x += 1) {
      const i = y * largeur + x;
      const l = gris[i - largeur]! + gris[i + largeur]! + gris[i - 1]! + gris[i + 1]! - 4 * gris[i]!;
      somme += l;
      carres += l * l;
      nombre += 1;
    }
  }
  if (nombre === 0) return 0;
  const moyenne = somme / nombre;
  return carres / nombre - moyenne * moyenne;
}

/**
 * En dessous, la photo est signalée comme floue et sa reprise proposée. Le
 * seuil est volontairement bas : une page de texte nette dépasse largement
 * cette valeur, et l'utilisateur peut toujours garder la photo.
 */
export const SEUIL_NETTETE = 60;
/** Largeur de la copie sur laquelle netteté et QR code sont évalués. */
export const LARGEUR_ANALYSE = 1000;

const UUID = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';
const MOTIF_LIEN_CHARTE = new RegExp(`/candidatures/(${UUID})\\?charte=(${UUID})(?:$|[&#])`, 'i');

export interface LienCharte {
  candidatureId: string;
  charteSigneeId: string;
}

/**
 * Le QR code d'une charte porte `<APP_URL>/candidatures/<id>?charte=<id>`.
 * L'hôte n'est pas vérifié ici — l'instance d'essai et la production n'ont pas
 * le même — : c'est l'API qui contrôle que la charte appartient bien au
 * candidat.
 */
export function lireLienCharte(texte: string): LienCharte | null {
  const trouve = MOTIF_LIEN_CHARTE.exec(texte.trim());
  if (!trouve) return null;
  return { candidatureId: trouve[1]!.toLowerCase(), charteSigneeId: trouve[2]!.toLowerCase() };
}

const MOTIF_LIEN_RAPPORT = new RegExp(`/candidatures/(${UUID})\\?rapport=(${UUID})(?:$|[&#])`, 'i');

export interface LienRapport {
  candidatureId: string;
  rapportId: string;
}

/** Le QR code d'un rapport de référence porte `<APP_URL>/candidatures/<id>?rapport=<id>`. */
export function lireLienRapport(texte: string): LienRapport | null {
  const trouve = MOTIF_LIEN_RAPPORT.exec(texte.trim());
  if (!trouve) return null;
  return { candidatureId: trouve[1]!.toLowerCase(), rapportId: trouve[2]!.toLowerCase() };
}
